import { BaseLogo, getLogoOrgId } from "../../../../core/models/entities/BaseLogo";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";
import { LogoDetails } from "../components/logo-details.component";
import { useLogoActionHandler } from "../hooks/useLogoActionsHandler";
import { useNavigate } from "react-router";
import { Verdict } from "../../../components/ApprovalVoting/approval-voting";
import { performCavemanReFetch } from "../../../../utils/functions/caveman-utils";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { Role } from "../../../../core/models/entities/Role";
import { useApiClient } from "../../../../data/clients/ApiClient";
import { useQueryClient } from "@tanstack/react-query";
import {
  dissmissToast,
  showErrorToast,
  showLoadingToast,
  showSuccessToast,
  withToasts,
} from "../../../components/Toast/Toast";
import { useTranslation } from "react-i18next";
import { useHealPendingHighRes } from "../../../../hooks/useHealPendingHighRes";
import { useProductPendingPoll } from "../../../../hooks/useProductPendingPoll";
import {
  isLogoDetailFromList,
  logoDetailKey,
  patchLogoRowsFromDetail,
  seedLogoDetailFromList,
} from "../../../../data/repositories/logoDetailCache";
import { useEffect, useRef, useState } from "react";
import { enviroment } from "../../../../enviroment";
import {
  isLogoProductPending,
  isLogoUploadPending,
} from "../../../../utils/functions/productPending";
import { useStorageFreshness } from "../../../../hooks/useStorageFreshness";
import { resolveStorageFreshnessOrgId } from "../../../../utils/functions/storageFreshness";
import { getActAsOrgId } from "../../../../contexts/actAsStore";
import {
  downgradeLogoDetailToStub,
  invalidateAfterLogoUnassign,
  patchLogoCustomerUnassigned,
} from "../../../../data/repositories/logoUnassignCache";
import { isUnassignNotLinked, unassignErrorText } from "../models/logoUnassign";

export function LogoDetailsWrapper({
  logo,
  onViewRejectNote,
  closeDialog,
  listSettled = true,
}: {
  logo: BaseLogo;
  onViewRejectNote: (logo: BaseLogo) => void;
  closeDialog: () => void;
  /**
   * The logo list's settled state (success, not fetching, not previous
   * data). The popup's stock-freshness check waits for it, so a deep-linked
   * popup joins the list's ONE call instead of firing its own first.
   */
  listSettled?: boolean;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { logoRepository } = useRepositoriesContext();
  const { profile, role, organizationId } = useProfileContext();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  // Flag REACT_APP_LOGO_POPUP_FAST: seed the detail entry from the list row
  // BEFORE useLogo below creates its observer (lazy initializer = runs once,
  // during this first render), so the query starts with data and no spinner
  // state ever exists. An existing detail (cached GetLogo, deep link,
  // mutation seed) is kept and nothing is written; a stub left by an earlier
  // open is seeded again (updatedAt 0, status success) so the observer loads
  // it on mount. Flag off = nothing written.
  useState(() => seedLogoDetailFromList(queryClient, logo));

  const {
    data: logoDetails,
    isError,
    refetch: refetchDetail,
  } = logoRepository.useLogo(getLogoOrgId(logo), logo.id);

  // The stub seeded above is complete for everything the popup shows except
  // designCount and the high-res render; the hooks below that must judge
  // the REAL GetLogo response get undefined until it lands. Never true with
  // the flag off (nobody seeds) - unless an unassign (its own flag) handed
  // the entry back as a stub, see handleUnassignCustomer.
  const detailIsStub = isLogoDetailFromList(logoDetails);
  const realDetail = detailIsStub ? undefined : logoDetails;

  // ONE background GetLogo to replace the stub - the same single call the
  // popup made today. Fired whenever the entry IS a stub, not only when this
  // popup seeded it: whoever wrote it and whatever its stamp, a stub on
  // screen always has a load on the way (refetchOnMount is off globally, so
  // an entry a writer stamped fresh would otherwise never load). The ref
  // fires it once per popup (StrictMode's double effect pass included) and
  // react-query dedupes it into the mount-load of an updatedAt-0 entry, so
  // it can never become two calls; a failed load leaves the popup in its
  // error state rather than retrying. handleUnassignCustomer sets the ref
  // BEFORE it hands the entry back as a stub: react-query notifies this
  // observer on its own tick and React 18 batches the parent's close commit
  // on another, in no guaranteed order - so without the guard the stub
  // transition could fire a GetLogo through the customer org that can no
  // longer read the logo while the popup is still mounted.
  const backgroundFetchFired = useRef(false);
  useEffect(() => {
    if (!detailIsStub || backgroundFetchFired.current) return;
    backgroundFetchFired.current = true;
    void refetchDetail();
  }, [detailIsStub, refetchDetail]);

  const {
    handleGoToEditLogo,
    handleDeleteLogo,
    handleChangeTitle,
    handlePlaceVerdict,
    handleAddToCart,
    handleCopyThenGoToLogo,
    handleSkipApproval,
    handleSendApproval,
    handleSendApprovalWithEndCustomerPending,
    isLoading: isActionRunning,
    handleRejectAndDelete,
  } = useLogoActionHandler();

  // Render optimistically with the logo the list already loaded (same entity,
  // same mapper - mapToLogo - as the detail query, incl. its browser-cached
  // low-res image) so the modal paints INSTANTLY, and swap to the full GetLogo
  // once the background fetch resolves. Previously the whole modal was gated
  // behind a spinner while GetLogo ran (~1.4-3.7s server-side). No extra
  // network: the list already fetched this logo. Only two things the popup
  // shows actually require the full fetch: designCount (list rows always carry
  // 0) and the fresh high-res image render - both handled via detailStatus
  // below, never presented with a wrong value meanwhile.
  const shownLogo = logoDetails ?? logo;
  // With REACT_APP_LOGO_POPUP_FAST the seeded stub counts as "loading" (its
  // designCount/high-res are not the detail's); without the flag no entry
  // is ever a stub and this is today's expression.
  const detailStatus = realDetail ? "ready" : isError ? "error" : "loading";

  // Stock freshness on view (flag): a stale storage row behind the popup's
  // count is refreshed in the background and patched into the detail entry
  // (["logos", actAs, id]) and the lists. 0 calls when the logo was just on
  // the list (asked there, or fresh by now); a deep link or a logo outside
  // the page rides along in the list's one call (same org timer), which is
  // why this waits for the list to settle. Flag off = no-op.
  useStorageFreshness(
    resolveStorageFreshnessOrgId(getActAsOrgId(), role, organizationId),
    shownLogo ? [shownLogo] : undefined,
    listSettled
  );

  // Fresh ImageLogo (upload/clone): the cached GetLogo can carry a blank or
  // identical hi-res because the logo system isn't done with the serial yet,
  // and nothing ever refetches it (refetchOnMount is off globally). Bounded
  // heal: up to three background refetches (5s/15s/45s), stopping as soon as
  // a genuine high-res lands or the popup closes. Judged on logoDetails (the
  // real detail response), never the list stub, so settled logos never poll.
  useHealPendingHighRes(logo.id, realDetail);

  // "Prices on their way" (flag productPendingEnabled): while the DETAIL
  // response says the product is still owed by the API worker, re-read
  // this popup's own GetLogo entry (["logos", actAs, id]) at 3/8/15 s -
  // max 3 calls, never the list - so the badge turns into prices without
  // a reload. Judged on logoDetails, never the list stub. Flag off = inert.
  // "Preparing logo…" (flag cloneInstantEnabled): the same bounded re-read
  // while the clone-upload worker still owes the clone its identity.
  const productPending =
    isLogoProductPending(realDetail) || isLogoUploadPending(realDetail);
  useProductPendingPoll(
    productPending ? [logoDetailKey(logo.id)] : undefined,
    productPending
  );

  // Whatever lands in the detail entry (the popup-fast background GetLogo,
  // the 3/8/15 s re-read above, the high-res heal) is copied into the LIST
  // row behind this popup - pending flags + product only, same act-as scope,
  // never a list newer than the detail - so the row drops its "Prices on
  // their way" the moment the popup does, instead of at the next list
  // refetch. 0 calls. Both flags off = no-op (nothing written).
  useEffect(() => {
    if (!realDetail) return;
    patchLogoRowsFromDetail(queryClient, realDetail);
  }, [realDetail, queryClient]);

  function handleViewDesign(logo: BaseLogo) {
    navigate(
      `/design?pageSize=25&pageIndex=1&filterBy=designlogos.logoid&filterOn=${logo.id}`
    );
  }

  function handleCreateDesign(logo: BaseLogo) {
    const params = new URLSearchParams({
      addLogoId: String(logo.id),
      logoTitle: logo.title,
    });
    navigate(`/design/create?${params.toString()}`);
  }

  function handleTitle(title: string) {
    handleChangeTitle(getLogoOrgId(logo), logo.id, title);
  }

  async function onDelete(logo: BaseLogo) {
    // No need for performCavemanReFetch - LogoRepository now handles optimistic updates
    try {
      await handleDeleteLogo(logo);
      closeDialog();
    } catch (error) {
      console.error("Failed to delete logo:", error);
    }
  }

  function onSendApproval(logo: BaseLogo, email: string) {
    // Use conditional approval for Customers to preserve existing approval statuses
    // Use regular approval for Vendors who have full control
    if (profile?.userOrganizationInformations.role === Role.EndUser) {
      handleSendApprovalWithEndCustomerPending(logo, email).then(
        performCavemanReFetch
      );
    } else {
      handleSendApproval(logo, email);
    }
  }

  function onRejectAndDelete(logo: BaseLogo, note?: string | undefined) {
    // No need for performCavemanReFetch - LogoRepository now handles optimistic updates
    return handleRejectAndDelete(logo, note);
  }

  function onPlaceVerdict(logo: BaseLogo, verdict: Verdict) {
    // No need for performCavemanReFetch - LogoRepository now handles optimistic updates
    return handlePlaceVerdict(logo, verdict);
  }

  async function handleAssignCustomer(customerId: number) {
    const transaction = async () => {
      await apiClient.assignCustomer(getLogoOrgId(logo), logo.id, customerId);
      // The ["logos"] prefix already covers every logo key (lists AND the
      // detail keys, which now carry the act-as segment: ["logos", actAs,
      // id]) — a second, id-specific invalidation would need that segment
      // and adds nothing (same reasoning as design-details.wrapper).
      await queryClient.invalidateQueries(["logos"]);
    };

    await withToasts(
      transaction,
      t("logoList.assigningCustomer"),
      t("logoList.customerAssigned"),
      t("logoList.customerAssignFailed")
    );
  }

  // "Unassign customer" (flag REACT_APP_LOGO_UNASSIGN_ENABLED): ONE DELETE
  // on the customer tab's route with the popup's customer org (the logo's
  // first customer link - the one the popup names). 204: the cached row and
  // detail drop the customer at once (0 calls; the detail becomes a stub
  // the next open loads through the fresh row's org), the popup closes -
  // its detail query reads the logo THROUGH that customer org, which may no
  // longer see it - and the lists refetch once (the 1 GET "Assign customer"
  // pays today); the customer tab's links entry is invalidated (0 calls
  // while off screen). 409 notRemovable: the API's reason is toasted,
  // nothing changes. 404 with the route's "not linked" body (removed
  // meanwhile, or a link the API sub-flag cannot classify): the detail
  // keeps its customer but becomes a stub too (the fresh row decides what
  // the next open shows), the popup closes and the lists refetch. Anything
  // else - a bodiless 404 (the API flag off, the route missing) included:
  // error toast only, nothing changes. Both cache writes are preceded by
  // the closing guard on the stub effect above, so the dying popup never
  // answers them with a GetLogo. The loading toast is dismissed exactly
  // once, right before the success or error toast, so the two never sit
  // side by side during the list refetch awaited after it (the finally
  // only covers a throw before either toast).
  async function handleUnassignCustomer() {
    const customer = shownLogo.customer;
    if (!customer || customer.type === "vendor") return;
    let loading: ReturnType<typeof showLoadingToast> | undefined =
      showLoadingToast(t("logoList.unassigningCustomer"));
    const settleLoading = () => {
      if (loading === undefined) return;
      dissmissToast(loading);
      loading = undefined;
    };
    try {
      await apiClient.detachCustomerLogo(customer.id, logo.id);
      backgroundFetchFired.current = true;
      patchLogoCustomerUnassigned(queryClient, logo.id);
      closeDialog();
      settleLoading();
      showSuccessToast(t("logoList.customerUnassigned"));
      await invalidateAfterLogoUnassign(queryClient, customer.id);
    } catch (error) {
      settleLoading();
      showErrorToast(unassignErrorText(error, t));
      if (isUnassignNotLinked(error)) {
        backgroundFetchFired.current = true;
        downgradeLogoDetailToStub(queryClient, logo.id);
        closeDialog();
        await invalidateAfterLogoUnassign(queryClient, customer.id);
      }
    } finally {
      settleLoading();
    }
  }

  return (
    <LogoDetails
      // Remount when the full GetLogo arrives so useState initializers
      // (order quantity, editable title) re-run against the complete data.
      // Both images are browser-cached by then, so the remount repaints
      // instantly (same guard as design-details.wrapper).
      // Flag REACT_APP_LOGO_POPUP_FAST: no remount - the detail is patched
      // into the same instance (props), the thumbnail stays on screen and
      // the high-res crossfades in; a min-quantity change from the detail
      // is applied by LogoDetails itself. Judged on the REAL detail: with
      // popup-fast off the only stub that can exist is the one an unassign
      // handed back (flag REACT_APP_LOGO_UNASSIGN_ENABLED), and its load
      // must remount like a first open; without stubs realDetail IS
      // logoDetails, so nothing changes with both flags off.
      key={
        enviroment.logoPopupFastEnabled
          ? "detail"
          : realDetail
            ? "summary"
            : "list-stub"
      }
      onPlaceVerdict={onPlaceVerdict}
      logo={shownLogo}
      detailStatus={detailStatus}
      onAddToCart={handleAddToCart}
      onShowRejectionNote={onViewRejectNote}
      onDeleteLogo={onDelete}
      onTitleChange={handleTitle}
      onViewDesigns={handleViewDesign}
      onEditLogo={handleGoToEditLogo}
      onCopyLogo={handleCopyThenGoToLogo}
      onSkipApproval={handleSkipApproval}
      onSendApproval={onSendApproval}
      // Only user actions overlay the modal - the background detail fetch
      // never gates the render. If it fails we keep the list data and show a
      // discreet per-field notice inside LogoDetails (detailStatus="error")
      // instead of replacing the whole popup with an error view.
      isLoading={isActionRunning}
      onRejectAndDelete={onRejectAndDelete}
      onAssignCustomer={handleAssignCustomer}
      onUnassignCustomer={handleUnassignCustomer}
      onCreateDesign={handleCreateDesign}
    />
  );
}
